/**
 * READ-ONLY SQLite reader, pure TypeScript and zero dependencies.
 *
 * The legacy Bible Strong app (React Native, Play Store 1.0.0) **has no
 * export or backup**: highlights live in an SQLite file
 * standard export from the app's private directory (`databases/highlight`). That file
 * IS the legacy format, so it must be readable in the browser.
 *
 * Deliberately minimal scope (scans two tiny tables):
 *   - traverses table b-tree (leaf pages `0x0D` and interior `0x05`);
 *   - decodifica el formato de registro (varints + serial types);
 *   - does NOT support overflow pages, WAL, or indexes — and when detecting
 *     unsupported features it **throws clearly**, never returning partial data.
 *
 * Sufficient and correct for the real legacy file (two tables, rows of
 * ~20 bytes, sin desbordamiento posible).
 */

/** Firma de un fichero SQLite 3 ("SQLite format 3\0"). */
const SQLITE_HEADER = "SQLite format 3\0";

/** Decoded cell value. */
export type SqliteValue = string | number | Uint8Array | null;

interface SqliteTable {
  columns: string[];
  rows: SqliteValue[][];
}

/** File is not a readable SQLite by this reader. */
export class SqliteReadError extends Error {}

/** Do these bytes start with a SQLite file header? */
export function looksLikeSqlite(bytes: Uint8Array): boolean {
  if (bytes.length < SQLITE_HEADER.length) {
    return false;
  }
  for (let i = 0; i < SQLITE_HEADER.length; i++) {
    if (bytes[i] !== SQLITE_HEADER.codePointAt(i)) {
      return false;
    }
  }
  return true;
}

/** SQLite varint (big-endian, 7 bits per byte, max 9 bytes). */
function readVarint(bytes: Uint8Array, offset: number): { value: number; size: number } {
  let value = 0;
  for (let i = 0; i < 8; i++) {
    const byte = bytes[offset + i];
    if (byte === undefined) {
      throw new SqliteReadError("Truncated varint");
    }
    value = value * 128 + (byte & 0x7f);
    if ((byte & 0x80) === 0) {
      return { value, size: i + 1 };
    }
  }
  const last = bytes[offset + 8];
  if (last === undefined) {
    throw new SqliteReadError("Truncated varint");
  }
  return { value: value * 256 + last, size: 9 };
}

/** Signed big-endian integer of `size` bytes. */
function readInt(bytes: Uint8Array, offset: number, size: number): number {
  let value = 0;
  for (let i = 0; i < size; i++) {
    const byte = bytes[offset + i];
    if (byte === undefined) {
      throw new SqliteReadError("Truncated integer");
    }
    value = value * 256 + byte;
  }
  // Two complement over `size` bytes.
  const limit = 2 ** (size * 8 - 1);
  return value >= limit ? value - limit * 2 : value;
}

const decoder = new TextDecoder();

/**
 * Decodes ONE row in SQLite "record" format.
 * `rowid` is passed separately because `INTEGER PRIMARY KEY` columns are stored
 * as NULL in record and actual value IS the rowid.
 */
function decodeRecord(payload: Uint8Array): SqliteValue[] {
  const header = readVarint(payload, 0);
  const serialTypes: number[] = [];
  let cursor = header.size;
  while (cursor < header.value) {
    const type = readVarint(payload, cursor);
    serialTypes.push(type.value);
    cursor += type.size;
  }

  const values: SqliteValue[] = [];
  let body = header.value;
  for (const type of serialTypes) {
    if (type === 0) {
      values.push(null);
    } else if (type >= 1 && type <= 4) {
      values.push(readInt(payload, body, type));
      body += type;
    } else if (type === 5) {
      values.push(readInt(payload, body, 6));
      body += 6;
    } else if (type === 6) {
      values.push(readInt(payload, body, 8));
      body += 8;
    } else if (type === 7) {
      values.push(new DataView(payload.buffer, payload.byteOffset + body, 8).getFloat64(0));
      body += 8;
    } else if (type === 8) {
      values.push(0);
    } else if (type === 9) {
      values.push(1);
    } else if (type >= 12 && type % 2 === 0) {
      const size = (type - 12) / 2;
      values.push(payload.subarray(body, body + size));
      body += size;
    } else if (type >= 13) {
      const size = (type - 13) / 2;
      values.push(decoder.decode(payload.subarray(body, body + size)));
      body += size;
    } else {
      throw new SqliteReadError(`Unsupported serial type ${type}`);
    }
  }
  return values;
}

interface Cell {
  rowid: number;
  values: SqliteValue[];
}

/**
 * Traverses table b-tree from `pageNumber` and collects all cells.
 * `usableSize` is page size minus reserved space at the end.
 */
function walkTableBTree(
  bytes: Uint8Array,
  pageSize: number,
  usableSize: number,
  pageNumber: number,
  out: Cell[],
  seen: Set<number>,
): void {
  if (seen.has(pageNumber)) {
    throw new SqliteReadError("Cycle in the b-tree");
  }
  seen.add(pageNumber);

  const base = (pageNumber - 1) * pageSize;
  // Page 1 starts after the 100-byte file header.
  const headerAt = pageNumber === 1 ? base + 100 : base;
  const pageType = bytes[headerAt];
  if (pageType === undefined) {
    throw new SqliteReadError(`Page ${pageNumber} is out of bounds`);
  }
  if (pageType !== 0x0d && pageType !== 0x05) {
    throw new SqliteReadError(
      `Page ${pageNumber} is not a table b-tree page (0x${pageType.toString(16)})`,
    );
  }

  const cellCount = readInt(bytes, headerAt + 3, 2);
  const isLeaf = pageType === 0x0d;
  const pointerArray = headerAt + (isLeaf ? 8 : 12);

  for (let i = 0; i < cellCount; i++) {
    const cellOffset = base + readInt(bytes, pointerArray + i * 2, 2);
    if (isLeaf) {
      const payloadSize = readVarint(bytes, cellOffset);
      const rowid = readVarint(bytes, cellOffset + payloadSize.size);
      const start = cellOffset + payloadSize.size + rowid.size;
      // No overflow support: detected and reported clearly.
      const maxLocal = usableSize - 35;
      if (payloadSize.value > maxLocal) {
        throw new SqliteReadError(
          "This database uses overflow pages, which the legacy importer cannot read",
        );
      }
      out.push({
        rowid: rowid.value,
        values: decodeRecord(bytes.subarray(start, start + payloadSize.value)),
      });
    } else {
      walkTableBTree(bytes, pageSize, usableSize, readInt(bytes, cellOffset, 4), out, seen);
    }
  }

  if (!isLeaf) {
    // Pointer to rightmost child (interior page header, offset 8).
    walkTableBTree(bytes, pageSize, usableSize, readInt(bytes, headerAt + 8, 4), out, seen);
  }
}

/** Extracts column names from a `CREATE TABLE`. */
function columnsFromDdl(sql: string): string[] {
  const open = sql.indexOf("(");
  const close = sql.lastIndexOf(")");
  if (open === -1 || close <= open) {
    return [];
  }
  const columns: string[] = [];
  let depth = 0;
  let current = "";
  for (const char of sql.slice(open + 1, close)) {
    if (char === "(") {
      depth++;
    } else if (char === ")") {
      depth--;
    }
    if (char === "," && depth === 0) {
      columns.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  columns.push(current);

  return columns
    .map((definition) => definition.trim())
    .filter((definition) => {
      const head = definition.split(/\s+/u)[0]?.toUpperCase() ?? "";
      // Descarta constraints de tabla (`PRIMARY KEY(...)`, `UNIQUE(...)`, …).
      return !["PRIMARY", "UNIQUE", "CHECK", "FOREIGN", "CONSTRAINT"].includes(head);
    })
    .map((definition) => (definition.split(/\s+/u)[0] ?? "").replaceAll(/["'`[\]]/gu, ""))
    .filter((name) => name.length > 0);
}

/**
 * Reads an entire table from an in-memory SQLite file.
 *
 * @throws {SqliteReadError} if file is not SQLite, uses unsupported
 *   features, or table does not exist.
 */
export function readSqliteTable(bytes: Uint8Array, tableName: string): SqliteTable {
  if (!looksLikeSqlite(bytes)) {
    throw new SqliteReadError("Not a SQLite file");
  }

  const rawPageSize = readInt(bytes, 16, 2);
  const pageSize = rawPageSize === 1 ? 65_536 : rawPageSize;
  if (pageSize < 512 || (pageSize & (pageSize - 1)) !== 0) {
    throw new SqliteReadError(`Invalid page size ${pageSize}`);
  }
  const usableSize = pageSize - (bytes[20] ?? 0);

  // `sqlite_master`: (type, name, tbl_name, rootpage, sql) on page 1.
  const master: Cell[] = [];
  walkTableBTree(bytes, pageSize, usableSize, 1, master, new Set());

  const entry = master.find((cell) => cell.values[0] === "table" && cell.values[1] === tableName);
  if (!entry) {
    throw new SqliteReadError(`Table "${tableName}" not found`);
  }
  const rootPage = entry.values[3];
  const ddl = entry.values[4];
  if (typeof rootPage !== "number" || typeof ddl !== "string") {
    throw new SqliteReadError(`Unreadable schema entry for "${tableName}"`);
  }

  const columns = columnsFromDdl(ddl);
  const cells: Cell[] = [];
  walkTableBTree(bytes, pageSize, usableSize, rootPage, cells, new Set());

  // An `INTEGER PRIMARY KEY` column aliases rowid: NULL in the record.
  const rowidAlias = columns.findIndex((_, index) => {
    const pattern = new RegExp(`\\b${columns[index]}\\b\\s+integer\\s+primary\\s+key`, "iu");
    return pattern.test(ddl);
  });

  return {
    columns,
    rows: cells.map((cell) => {
      const values = [...cell.values];
      if (rowidAlias !== -1 && values[rowidAlias] === null) {
        values[rowidAlias] = cell.rowid;
      }
      return values;
    }),
  };
}

/** Reads a table and returns it as `{ column: value }` objects. */
export function readSqliteRows(
  bytes: Uint8Array,
  tableName: string,
): Record<string, SqliteValue>[] {
  const table = readSqliteTable(bytes, tableName);
  return table.rows.map((row) => {
    const record: Record<string, SqliteValue> = {};
    for (const [index, column] of table.columns.entries()) {
      record[column] = row[index] ?? null;
    }
    return record;
  });
}
